import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZrmPropertyUnitsWebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-client';

import { cleanApiParams } from '../properties/zrm';

import type {
  AccountViewPropertyUnitResponseDto,
  AccountViewPropertyUnitsResponseDto,
  ListingCountDto,
  RentalManagerKeyPagerDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type PropertyTableUnitsViewParams = {
  includeArchived?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
};

export type UseGetPropertyTableUnitsListOptions = {
  enabled?: boolean;
};

// Flattened shape returned when pages are loaded; empty object when no data yet.
export type PropertyTableUnitsListData = {
  units?: Array<AccountViewPropertyUnitResponseDto>;
  listingCount?: ListingCountDto;
  pager?: RentalManagerKeyPagerDto;
};

/**
 * Top-level flat units list for the "Units" view mode: fetches every unit across the account from
 * the property-table units endpoint with infinite-query pagination (mirrors {@link useGetPropertyTableList}).
 */
export const useGetPropertyTableUnitsList = (
  params: PropertyTableUnitsViewParams,
  options: UseGetPropertyTableUnitsListOptions = {},
) => {
  const { enabled = true } = options;
  const { includeArchived, includeUnarchived, limit, searchText = '' } = params;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } =
    useInfiniteQuery<
      AccountViewPropertyUnitsResponseDto,
      Error,
      { pages: Array<AccountViewPropertyUnitsResponseDto> },
      Array<boolean | string | number | undefined>,
      string
    >({
      queryKey: ['getPropertyTableUnitsList', searchText, includeArchived, includeUnarchived, limit],
      queryFn: async ({ pageParam }) => {
        return RentalManagerAccountViewZrmPropertyUnitsWebApiHandler_list(
          cleanApiParams(
            { limit, searchText, includeArchived, includeUnarchived, startKeyExclusive: pageParam || undefined },
            ['limit', 'searchText', 'includeArchived', 'includeUnarchived', 'startKeyExclusive'],
          ),
        );
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      enabled,
      // Keep previous data during refetch to prevent UI flicker
      placeholderData: (previousData) => {
        if (!previousData?.pages?.length) {
          return;
        }

        return previousData;
      },
      meta: {
        tags: ['getPropertyTableUnitsList'],
        context: {
          api: 'RentalManagerAccountViewZrmPropertyUnitsWebApiHandler_list',
          details: 'useGetPropertyTableUnitsList',
        },
      },
    });

  const unitsData: PropertyTableUnitsListData = data?.pages.length
    ? {
        units: data.pages.flatMap((page) => page.units),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: unitsData,
    error,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
